import {
  CURRENCY_MAX_DIGITS,
  type InventoryInstanceWire,
  type ReservationPurpose,
  SOVEREIGN_JEWEL_DAMAGE_BPS,
} from '@kenocyber/shared';

/**
 * O modelo da incorporação de um Coração Primordial (gate V28F.C).
 *
 * ## Porque isto é um módulo puro
 *
 * Porque a parte que pode estar errada não é o desenho: é a aritmética. Uma
 * contagem de corações é `numeric(40, 0)` no banco, `bigint` no servidor e uma
 * **string decimal** no fio, e a interface tem de somar UM a ela para dizer
 * "N → N+1" sem nunca a converter. Um `Number` acima de 2^53 mente em silêncio,
 * e um `+1` sobre uma string concatena.
 *
 * Nada aqui importa React, toca no DOM ou faz um pedido. O que este ficheiro
 * decide é apenas: que naves podem receber um coração, o que cada carta diz, e
 * que frase um jogador lê quando o servidor recusa.
 *
 * ## O que ele NÃO decide
 *
 * Não decide se a operação vai passar. O servidor tranca a nave e o coração e
 * volta a verificar tudo - uma nave que aqui parece elegível pode ter sido
 * anunciada entretanto, e é por isso que a recusa tem de ter uma frase e o modal
 * tem de ficar aberto. Esta lista é o que o cliente SABE, nunca uma promessa.
 */

/** Um por cento por coração, em basis points, tal como o domínio o define. */
const BPS_PER_HEART = BigInt(SOVEREIGN_JEWEL_DAMAGE_BPS);

/**
 * Cem basis points é UM PONTO PERCENTUAL.
 *
 * Não dez mil: dez mil é o inteiro, `BPS_ONE`, e dividir por ele daria a
 * fracção (0,03) onde se quer a percentagem (3). A primeira versão desta função
 * fazia exactamente isso, e um coração aparecia como "+0,01%".
 */
const BPS_PER_PERCENT = 100n;

/**
 * O tecto de ARMAZENAMENTO da contagem: os mesmos 40 dígitos das moedas.
 *
 * Não é um tecto de jogo - não existe ponto em que um coração deixe de valer
 * +1%. É o que a coluna `numeric(40, 0)` guarda, e o servidor recusa antes da
 * base com `heart_capacity_exceeded`. A interface diz-lho por palavras em vez de
 * oferecer um gesto que acabaria numa recusa.
 */
const MAX_HEARTS = 10n ** BigInt(CURRENCY_MAX_DIGITS) - 1n;

/**
 * Lê uma contagem do fio.
 *
 * `BigInt('')` é `0n` e `BigInt('1.5')` atira - as duas coisas erradas em
 * silêncio se ninguém verificar. Uma contagem é uma sequência de dígitos, e
 * qualquer outra coisa é um contrato partido, não um valor a arredondar.
 */
export function parseHeartCount(value: string): bigint {
  if (!/^\d+$/.test(value)) {
    throw new RangeError(
      `a primordial heart count is a decimal string, got ${JSON.stringify(value)}`,
    );
  }
  return BigInt(value);
}

/**
 * A percentagem que N corações valem, como string decimal.
 *
 * Exacta em `bigint`, e por isso legível com trinta e um dígitos ou com
 * quarenta. `SOVEREIGN_JEWEL_DAMAGE_BPS` é 100 - um por cento - portanto a
 * conta é uma divisão exacta hoje; ela é escrita assim mesmo para que o dia em
 * que a constante mudar não seja o dia em que a interface passe a mentir.
 */
export function heartBonusPercent(count: bigint): string {
  const bps = count * BPS_PER_HEART;
  const whole = bps / BPS_PER_PERCENT;
  const fraction = bps % BPS_PER_PERCENT;
  if (fraction === 0n) {
    return whole.toString();
  }
  /* Duas casas, sem zeros à direita: 150 bps é "1,5", nunca "1,50". */
  const digits = fraction.toString().padStart(2, '0').replace(/0+$/, '');
  return `${whole},${digits}`;
}

/** Porque é que uma nave não pode receber um coração agora. */
export type ShipBlockReason =
  | 'reserved_market'
  | 'reserved_trade'
  | 'reserved_merge'
  | 'reserved_system'
  | 'reserved'
  | 'at_capacity';

/**
 * A frase que o jogador lê, por razão.
 *
 * `Nave reservada no mercado` é literal e obrigatória: é o caso que o gate
 * nomeia. As outras três existem porque uma reserva pode ser de uma troca, de
 * uma fusão ou de uma operação de sistema, e chamar mercado a qualquer uma delas
 * seria dizer ao jogador que a nave está à venda quando não está.
 */
export const SHIP_BLOCK_TEXT: Record<ShipBlockReason, string> = {
  reserved_market: 'Nave reservada no mercado',
  reserved_trade: 'Nave reservada numa troca',
  reserved_merge: 'Nave reservada numa fusão',
  reserved_system: 'Nave reservada por uma operação',
  reserved: 'Nave reservada',
  at_capacity: 'Nave no limite de Corações que o registo guarda',
};

function reservationBlock(purpose: ReservationPurpose | null): ShipBlockReason {
  switch (purpose) {
    case 'market_listing':
      return 'reserved_market';
    case 'direct_trade':
      return 'reserved_trade';
    case 'merge':
      return 'reserved_merge';
    case 'system_operation':
      return 'reserved_system';
    default:
      return 'reserved';
  }
}

/** Uma nave da conta, como o seletor a mostra. */
export interface PrimordialShipOption {
  readonly shipInstanceId: string;
  /** O nome que o resto do inventário já usa para esta cópia. */
  readonly name: string;
  readonly level: number;
  readonly equipped: boolean;
  /** Ligada à conta pela proveniência: nunca sai daqui. */
  readonly soulbound: boolean;
  /** Negociável no catálogo E não ligada. As duas coisas, ou não é. */
  readonly tradable: boolean;
  readonly heartCount: string;
  readonly heartCountAfter: string;
  readonly bonusPercent: string;
  readonly bonusPercentAfter: string;
  readonly eligible: boolean;
  readonly blockReason: ShipBlockReason | null;
  /** A frase pronta a ler, ou `null` quando não há nada a explicar. */
  readonly blockText: string | null;
}

/**
 * As naves da conta, prontas para o seletor.
 *
 * ## A contagem vem do inventário, não de uma segunda lista
 *
 * `InventoryInstanceWire.primordialHeartCount` já traz a contagem por cópia -
 * `null` para o que não é nave, `'0'` para uma nave sem nenhum. Cruzar isto com
 * a lista `ships` da view das joias daria duas fontes para o mesmo facto, e duas
 * fontes divergem no primeiro caso que ninguém previu.
 *
 * ## Ordem: a equipada primeiro, depois por nome e nível
 *
 * A equipada primeiro porque é a que o jogador tem em mente. Nunca escolhida
 * SOZINHA por isso: a incorporação é irreversível, e um gesto irreversível que
 * adivinha o alvo é um gesto que um dia acerta no alvo errado.
 */
export function buildShipOptions(
  instances: readonly InventoryInstanceWire[],
): PrimordialShipOption[] {
  const ships = instances.filter(
    (item) => item.category === 'ship' && item.primordialHeartCount !== null,
  );
  const options = ships.map((ship) => {
    const count = parseHeartCount(ship.primordialHeartCount as string);
    const after = count + 1n;
    const block: ShipBlockReason | null = ship.locked
      ? reservationBlock(ship.lockedPurpose)
      : after > MAX_HEARTS
        ? 'at_capacity'
        : null;
    return {
      shipInstanceId: ship.itemId,
      name: ship.definitionId,
      level: ship.level,
      equipped: ship.equipped,
      soulbound: ship.soulbound,
      /* Ligada é ligada: um item soulbound nunca é negociável, seja o que for
         que o catálogo diga da definição. */
      tradable: ship.tradable && !ship.soulbound,
      heartCount: count.toString(),
      heartCountAfter: after.toString(),
      bonusPercent: heartBonusPercent(count),
      bonusPercentAfter: heartBonusPercent(after),
      eligible: block === null,
      blockReason: block,
      blockText: block === null ? null : SHIP_BLOCK_TEXT[block],
    } satisfies PrimordialShipOption;
  });
  return options.sort((left, right) => {
    if (left.equipped !== right.equipped) {
      return left.equipped ? -1 : 1;
    }
    if (left.name !== right.name) {
      return left.name.localeCompare(right.name);
    }
    return left.level - right.level;
  });
}

/**
 * A frase para cada recusa do servidor.
 *
 * ## Estável, e por nome
 *
 * A razão viaja no corpo do erro (`ship_reserved`, `heart_already_consumed`) e é
 * ela que escolhe o texto - nunca o código HTTP. Dois 409 diferentes querem
 * dizer coisas diferentes ao jogador, e "conflito" não é uma delas.
 *
 * ## E nenhuma delas promete nada
 *
 * Nenhuma frase aqui sugere desfazer, recuperar ou tentar outra vez algo que não
 * vai passar. Uma nave anunciada tem de sair do mercado primeiro, e é isso que a
 * frase diz.
 */
export const INCORPORATION_FAILURE_TEXT: Record<string, string> = {
  ship_reserved: 'Esta nave está reservada no mercado. Cancele o anúncio antes de incorporar.',
  ship_not_found: 'Esta nave já não está no seu inventário.',
  ship_not_owned: 'Esta nave já não está no seu inventário.',
  not_a_ship: 'Esta cópia não é uma nave.',
  heart_already_consumed: 'Este Coração Primordial já foi incorporado.',
  heart_reserved: 'Este Coração Primordial está reservado por outra operação.',
  jewel_not_found: 'Este Coração Primordial já não está no seu inventário.',
  jewel_not_owned: 'Este Coração Primordial já não está no seu inventário.',
  not_a_primordial_heart: 'Esta peça não é um Coração Primordial.',
  heart_capacity_exceeded: 'Esta nave já guarda o número máximo de Corações que o registo permite.',
  idempotency_conflict: 'Um pedido diferente já usou esta chave. Recomece a incorporação.',
};

/** O que dizer quando o servidor recusou sem um nome que conheçamos. */
export const INCORPORATION_UNKNOWN_FAILURE = 'O servidor recusou a incorporação.';

export function incorporationFailureText(reason: string | null): string {
  return (reason ? INCORPORATION_FAILURE_TEXT[reason] : null) ?? INCORPORATION_UNKNOWN_FAILURE;
}

/**
 * As cinco frases do modal, e são um contrato com o jogador.
 *
 * Literais, e verificáveis no código uma a uma: o coração é gasto
 * (`consumeInstance` escreve o estado terminal), a nave ganha +1% permanente
 * (`primordialHeartBps`), não ocupa socket (`socketMode: 'sovereign'`), não há
 * rota inversa (nenhuma existe), e a contagem viaja com a nave numa venda
 * (a linha é indexada por `instance_id`, provado em `primordial-listing-db`).
 *
 * Aqui e não no JSX para que um teste possa afirmar o TEXTO sem montar nada, e
 * para que mudá-lo seja uma decisão visível num diff.
 */
export const INCORPORATION_TERMS = [
  'Esta ação consome permanentemente 1 Coração Primordial.',
  /*
   * "O dano CAUSADO", e nunca "a nave receberá dano".
   *
   * A frase anterior dizia "A nave receberá +1% de dano permanente", que se lê
   * ao contrário: uma nave que recebe dano é uma nave que está a ser atingida.
   * O que o Coração faz é o oposto, e num modal irreversível a frase tem de
   * dizer qual dos dois é.
   *
   * O vocabulário é o mesmo em todo o lado onde este bónus aparece - a carta da
   * nave diz "de dano causado" pela mesma razão.
   */
  'O dano causado pela nave aumentará permanentemente em 1%.',
  'Não ocupa socket.',
  'Não pode ser removido nem transferido separadamente.',
  'Se a nave for vendida, todos os Corações incorporados acompanham a nave.',
] as const;

/** A frase do checkbox, exigida antes de o botão final abrir. */
export const INCORPORATION_ACKNOWLEDGEMENT = 'Entendo que esta ação é permanente.';
